/**
 * Top-of-sidebar row: current workspace + a down-opening switcher menu.
 * Squared-off avatar (8px radius) on purpose — accounts are organisations,
 * not people, which is also why the trailing glyph is
 * ChevronSelectorVertical ("swap this"), not DotsVertical ("a list of
 * actions") — see AccountRow.
 * Spec: assets/handoffs/design_handoff_app_shell, Rules — shell / collapse.
 *
 * @param {{uuid,name,initials,current}[]} workspaces
 * @param {{uuid,name,initials}} current
 * @param {function} onSwitch - (uuid) => void
 * @param {string=} addWorkspaceHref
 * @param {string=} workspaceSettingsHref
 * @param {boolean=false} collapsed
 * @param {boolean=false} disabled - true while impersonating; switching from
 *   inside another account's session is ambiguous
 */
import React from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import Icon from "../../../theme/untitled/icons/Icon";
import { useDismissablePanel } from "../useDismissablePanel";
import styles from "./WorkspaceSwitcher.module.scss";

const SquareAvatar = ({ initials, icon, size }) => (
  <span className={classNames(styles.squareAvatar, styles[`squareAvatar--${size}`])}>
    {icon ? <img src={icon} alt="" className={styles.squareAvatarImg} /> : initials}
  </span>
);

const WorkspaceSwitcher = ({
  workspaces = [],
  current,
  onSwitch,
  addWorkspaceHref = "/new-organization",
  workspaceSettingsHref = "/organization-settings",
  collapsed = false,
  disabled = false,
}) => {
  const { open, setOpen, triggerRef, panelRef, close } = useDismissablePanel();

  const handleSwitch = (uuid) => {
    close(false);
    onSwitch?.(uuid);
  };

  const menu = (
    <div ref={panelRef} className={classNames(styles.panel, { [styles["panel--flyout"]]: collapsed })}>
      <div className={styles.panelLabel}>Switch workspace</div>
      <div className={styles.list}>
        {workspaces.map((ws) => (
          <button
            key={ws.uuid}
            type="button"
            className={styles.item}
            onClick={() => handleSwitch(ws.uuid)}
          >
            <SquareAvatar initials={ws.initials} icon={ws.icon} size="sm" />
            <span className={styles.itemLabel}>{ws.name}</span>
            {ws.uuid === current?.uuid && (
              <Icon name="Check" className={styles.check} />
            )}
          </button>
        ))}
      </div>
      <div className={styles.footer}>
        <Link to={workspaceSettingsHref} className={styles.footerLink} onClick={() => close(false)}>
          <Icon name="Settings01" className={styles.addIcon} />
          Workspace settings
        </Link>
        <Link to={addWorkspaceHref} className={styles.footerLink} onClick={() => close(false)}>
          <Icon name="Plus" className={styles.addIcon} />
          Add workspace
        </Link>
      </div>
    </div>
  );

  if (collapsed) {
    return (
      <div className={styles.collapsedWrap}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.collapsedTrigger}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <SquareAvatar initials={current?.initials} icon={current?.icon} size="md" />
        </button>
        {open && menu}
      </div>
    );
  }

  return (
    <div className={styles.wrap}>
      <button
        ref={triggerRef}
        type="button"
        className={classNames(styles.row, { [styles["row--open"]]: open })}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <SquareAvatar initials={current?.initials} icon={current?.icon} size="md" />
        <span className={styles.name}>{current?.name}</span>
        <Icon name="ChevronSelectorVertical" className={styles.chevron} />
      </button>
      {open && menu}
    </div>
  );
};

export default WorkspaceSwitcher;
